import { useEffect, useRef, useState } from "react";
import { X, RotateCcw, RotateCw, Check, Trash2, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";

async function bakeRotation(blob, rot) {
  if (!rot) return blob;
  const bmp = await createImageBitmap(blob);
  const swap = rot % 180 !== 0;
  const c = document.createElement("canvas");
  c.width = swap ? bmp.height : bmp.width;
  c.height = swap ? bmp.width : bmp.height;
  const ctx = c.getContext("2d");
  ctx.translate(c.width / 2, c.height / 2);
  ctx.rotate((rot * Math.PI) / 180);
  ctx.drawImage(bmp, -bmp.width / 2, -bmp.height / 2);
  return new Promise((res) => c.toBlob((b) => res(b || blob), "image/jpeg", 0.92));
}

// Full-screen preview of an unsaved capture: pinch/wheel zoom, clamped pan,
// rotate, reset, retake, keep, delete, prev/next.
export default function CapturePreview({ shots, index, setIndex, onClose, onDelete, onRetake, onKeep }) {
  const { t } = useTranslation();
  const shot = shots[index];
  const frameRef = useRef(null);
  const imgRef = useRef(null);
  const pointersRef = useRef(new Map());
  const pinchRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [rot, setRot] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setScale(1); setTx(0); setTy(0); setRot(0); }, [index]);

  const getBounds = (s, r) => {
    const frame = frameRef.current?.getBoundingClientRect();
    const img = imgRef.current;
    if (!frame || !img) return { maxX: 0, maxY: 0 };
    const natW = img.naturalWidth || 1;
    const natH = img.naturalHeight || 1;
    const swap = r % 180 !== 0;
    const dw = swap ? natH : natW;
    const dh = swap ? natW : natH;
    const fit = Math.min(frame.width / dw, frame.height / dh);
    const w = dw * fit * s;
    const h = dh * fit * s;
    return { maxX: Math.max(0, (w + frame.width) / 2 - 48), maxY: Math.max(0, (h + frame.height) / 2 - 48) };
  };

  const clampAll = (nTx, nTy, s, r) => {
    const { maxX, maxY } = getBounds(s, r);
    return [Math.max(-maxX, Math.min(maxX, nTx)), Math.max(-maxY, Math.min(maxY, nTy))];
  };

  const applyZoom = (ns) => {
    const clamped = Math.min(4, Math.max(1, ns));
    const [cx, cy] = clampAll(tx, ty, clamped, rot);
    setScale(clamped);
    setTx(cx);
    setTy(cy);
  };

  const onPointerDown = (e) => {
    if (e.target.closest("button")) return;
    try { frameRef.current?.setPointerCapture?.(e.pointerId); } catch { /* synthetic/invalid pointer ids */ }
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      pinchRef.current = { d: Math.hypot(a.x - b.x, a.y - b.y), s: scale };
    }
  };

  const onPointerMove = (e) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    const prev = pointersRef.current.get(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2 && pinchRef.current) {
      const [a, b] = [...pointersRef.current.values()];
      applyZoom(pinchRef.current.s * (Math.hypot(a.x - b.x, a.y - b.y) / pinchRef.current.d));
    } else if (pointersRef.current.size === 1 && scale > 1) {
      const [cx, cy] = clampAll(tx + e.clientX - prev.x, ty + e.clientY - prev.y, scale, rot);
      setTx(cx);
      setTy(cy);
    }
  };

  const onPointerUp = (e) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    if (scale <= 1.01) { setScale(1); setTx(0); setTy(0); }
  };

  const onWheel = (e) => applyZoom(scale * (e.deltaY < 0 ? 1.12 : 0.9));

  const rotate = (d) => {
    const nr = (rot + d + 360) % 360;
    setRot(nr);
    const [cx, cy] = clampAll(tx, ty, scale, nr);
    setTx(cx);
    setTy(cy);
  };

  const reset = () => { setScale(1); setTx(0); setTy(0); setRot(0); };

  const keep = async () => {
    setBusy(true);
    try {
      if (rot !== 0) {
        const baked = await bakeRotation(shot.blob, rot);
        onKeep(index, baked, URL.createObjectURL(baked));
      }
    } catch (e) { /* keep original on failure */ }
    setBusy(false);
    onClose();
  };

  if (!shot) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black flex flex-col" data-testid="cam-preview">
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/70 to-transparent absolute top-0 inset-x-0 z-10">
        <button onClick={onClose} data-testid="pv-close" aria-label={t("cam_close")}
          className="rounded-full bg-slate-800/80 p-3 text-white min-h-[44px] min-w-[44px] flex items-center justify-center">
          <X className="h-5 w-5" />
        </button>
        <div className="text-white text-sm font-bold" data-testid="pv-counter">{index + 1} / {shots.length}</div>
        <button onClick={() => onDelete(index)} data-testid="pv-delete" aria-label={t("cam_delete")}
          className="rounded-full bg-red-600/90 p-3 text-white min-h-[44px] min-w-[44px] flex items-center justify-center">
          <Trash2 className="h-5 w-5" />
        </button>
      </div>

      <div
        ref={frameRef}
        className="flex-1 relative overflow-hidden touch-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
        data-testid="pv-frame"
      >
        <img
          ref={imgRef}
          src={shot.url}
          alt={t("cam_preview")}
          draggable={false}
          className="absolute inset-0 h-full w-full object-contain select-none"
          style={{ transform: `translate(${tx}px, ${ty}px) scale(${scale}) rotate(${rot}deg)`, transformOrigin: "center" }}
          data-testid="pv-image"
        />
        {shots.length > 1 && (
          <>
            <button onClick={() => setIndex((index - 1 + shots.length) % shots.length)} data-testid="pv-prev" aria-label={t("cam_prev")}
              className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/60 border border-white/30 p-3 text-white min-h-[44px] min-w-[44px]">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button onClick={() => setIndex((index + 1) % shots.length)} data-testid="pv-next" aria-label={t("cam_next")}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/60 border border-white/30 p-3 text-white min-h-[44px] min-w-[44px]">
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 px-4 py-4 bg-black/80 pb-[max(env(safe-area-inset-bottom,8px),16px)] flex-wrap">
        <button onClick={() => rotate(-90)} data-testid="pv-rotate-left" aria-label={t("cam_rotate_left")}
          className="rounded-full bg-slate-800 p-3 text-white min-h-[44px] min-w-[44px] flex items-center justify-center">
          <RotateCcw className="h-5 w-5" />
        </button>
        <button onClick={() => rotate(90)} data-testid="pv-rotate-right" aria-label={t("cam_rotate_right")}
          className="rounded-full bg-slate-800 p-3 text-white min-h-[44px] min-w-[44px] flex items-center justify-center">
          <RotateCw className="h-5 w-5" />
        </button>
        <button onClick={reset} data-testid="pv-reset" aria-label={t("cam_reset")}
          className="rounded-full bg-slate-800 p-3 text-white min-h-[44px] min-w-[44px] flex items-center justify-center">
          <RefreshCw className="h-5 w-5" />
        </button>
        <span className="w-2" />
        <button onClick={() => onRetake(index)} data-testid="pv-retake"
          className="inline-flex items-center gap-2 rounded-full border border-white/40 px-5 py-2.5 font-bold text-white min-h-[48px] active:scale-95">
          {t("cam_retake")}
        </button>
        <button onClick={keep} disabled={busy} data-testid="pv-keep"
          className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 font-bold text-white min-h-[48px] active:scale-95 disabled:opacity-50 bf-brand-shadow">
          <Check className="h-5 w-5" /> {t("cam_keep")}
        </button>
      </div>
    </div>
  );
}
